import { ButtonLink } from '@designsforhealth/dfh-react-components'
import { Subscribe } from '@react-rxjs/core'
import React, { useEffect } from 'react'

import useRetailPrices from '../../../../hooks/useRetailPrices'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import { cartItemDisplayName } from '../../../../lib/cart/item'
import {
  latestCartItemProduct$,
  setCartItemSku,
  useCartItemProduct,
} from '../../../../lib/cart/state/cart-items/cart-item-product'
import { useAutoUpdateErrantCartItems } from '../../../../lib/cart/state/cart-items/cart-item-update'
import useSanityImage from '../../../../lib/sanity/hooks/useSanityImage'
import { itemFlavorText } from '../../../../utils/itemTools'
import { cartItemAndDesignFeePrice, formatAsCurrency } from '../../../../utils/priceTools'
import CartItemDescription from '../../../cart/CartItemDescription'
import ItemLoadingOverlay from '../../../cart/ItemLoadingOverlay'
import RequireLabelStatusPresent from '../../../cart/RequireLabelStatusPresent'
import StockAlertAndModal from '../../../cart/StockAlertAndModal'
import { CartItemProps } from '../../../cart/types'
import ErrorUpdatingCartMessage from '../../../ErrorUpdatingCartMessage'
import CartItemDescriptionModal from '../CartItemDescriptionModal'
import CartItemLabelInformation from '../CartItemLabelInformation'
import ItemImage from '../ItemImage'
import * as Styled from './styled'

const CartItemRetailPrice: React.FC<{ sku: string }> = ({ sku }) => {
  const retailPrice = useCartItemProduct(sku).retailPrice
  if (!retailPrice) {
    return null
  }
  const amount = parseFloat(retailPrice.amount)
  if (Number.isNaN(amount)) {
    return null
  }
  return (
    <Styled.RetailPrice>
      <b>{formatAsCurrency({ amount })}</b> retail
    </Styled.RetailPrice>
  )
}

const CartItemImage: React.FC<{ item: CartItemProps['item'] }> = ({ item }) => {
  const image = useCartItemProduct(item.product.sku ?? '').mainImage

  const sanityImageUrl = useSanityImage(
    { sourceJson: image?.json },
    {
      dpr: 3,
      fit: 'max',
      // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
      width: 70,
    },
  )
  return (
    <ItemImage
      altText={item.product.name ?? ''}
      itemUrlKey={item.product.url_key}
      src={sanityImageUrl ?? ''}
    />
  )
}

const CartItem: React.FC<CartItemProps> = ({
  className,
  deleteCartItem,
  deleteError,
  item,
  labelDesignFeeItem,
  loading,
  updateCartItem,
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const { loading: autoUpdateItemsLoading, itemsPendingUpdate } = useAutoUpdateErrantCartItems()
  const isItemPendingUpdate = !!itemsPendingUpdate.find((cartItem) => cartItem.id === item.id)
  const itemIsLoading = loading || (autoUpdateItemsLoading && isItemPendingUpdate)
  const isProductLabelStatusEnabled = !!featureFlags?.productLabelStatus

  useEffect(() => {
    if (item.product.sku) {
      setCartItemSku(item.product.sku)
    }
  }, [item.product.sku])

  return (
    <Styled.CartItemComponent className={className}>
      {/* wrap controls in null form since input names are not unique per item */}
      <Styled.ItemForm noValidate onSubmit={(e) => e.preventDefault()}>
        <Styled.ItemHeader>
          {item.product.sku && (
            <Subscribe source$={latestCartItemProduct$(item.product.sku)} fallback={null}>
              <CartItemImage item={item} />
            </Subscribe>
          )}
          <div>
            <Styled.OptionalProductLink urlKey={item.product.url_key}>
              <h4>{cartItemDisplayName({ item })}</h4>
            </Styled.OptionalProductLink>
            {item.item_attributes?.dosage && (
              <Styled.DosageTag>{item.item_attributes.dosage}</Styled.DosageTag>
            )}
            {itemFlavorText({ itemAttributes: item.item_attributes }) && (
              <Styled.FlavorTag>
                {itemFlavorText({ itemAttributes: item.item_attributes })}
              </Styled.FlavorTag>
            )}
            {item.item_attributes?.size && (
              <Styled.SizeTag>{item.item_attributes.size}</Styled.SizeTag>
            )}
            <CartItemDescription item={item} modalComponent={CartItemDescriptionModal} />
          </div>
          <Styled.Price $productLabelStatus={isProductLabelStatusEnabled}>
            {isProductLabelStatusEnabled ? (
              <>
                <Styled.Total>
                  <strong>{`Total: ${cartItemAndDesignFeePrice(item, labelDesignFeeItem)}`}</strong>
                </Styled.Total>
                <Styled.PriceBreakdown>
                  {`Unit price: ${formatAsCurrency({ amount: item.prices?.price.value || 0 })}`}
                </Styled.PriceBreakdown>
                {!!labelDesignFeeItem && (
                  <Styled.PriceBreakdown>
                    {`Design Fee: ${formatAsCurrency({
                      amount: labelDesignFeeItem.prices?.row_total.value || 0,
                    })}`}
                  </Styled.PriceBreakdown>
                )}
              </>
            ) : (
              <>
                {item.product.sku && shouldDisplayRetailPrice && (
                  <Subscribe source$={latestCartItemProduct$(item.product.sku)} fallback={null}>
                    <CartItemRetailPrice sku={item.product.sku} />
                  </Subscribe>
                )}
                <span>{formatAsCurrency({ amount: item.prices?.price.value || 0 })}</span>
              </>
            )}
          </Styled.Price>
        </Styled.ItemHeader>
        <Styled.OptionsContainer>
          {isProductLabelStatusEnabled ? (
            <Subscribe fallback={null}>
              <RequireLabelStatusPresent sku={item.product.sku || ''}>
                <Styled.CartItemOrderTypeControl
                  disabled={itemIsLoading}
                  item={item}
                  updateCartItem={updateCartItem}
                />
              </RequireLabelStatusPresent>
            </Subscribe>
          ) : (
            <Styled.CartItemOrderTypeControl
              disabled={itemIsLoading}
              item={item}
              updateCartItem={updateCartItem}
            />
          )}
          <Styled.QuantityAndRemoveButtonContainer>
            <Styled.CartItemQuantityControl
              disabled={itemIsLoading}
              item={item}
              updateCartItem={updateCartItem}
            />
            <Styled.RemoveLink>
              <ButtonLink
                className="small-paragraph-text"
                disabled={itemIsLoading}
                onClick={deleteCartItem}
                text="Remove"
                underline
              />
            </Styled.RemoveLink>
          </Styled.QuantityAndRemoveButtonContainer>
        </Styled.OptionsContainer>
        {isProductLabelStatusEnabled && (
          <Subscribe fallback={null}>
            <CartItemLabelInformation sku={item.product.sku || ''} labelItem={labelDesignFeeItem} />
          </Subscribe>
        )}
        {deleteError && (
          <Styled.Alert type="error">
            <ErrorUpdatingCartMessage />
          </Styled.Alert>
        )}
        <StockAlertAndModal item={item} />
      </Styled.ItemForm>
      {itemIsLoading && <ItemLoadingOverlay />}
    </Styled.CartItemComponent>
  )
}

export default CartItem
